import type { Locale } from '../entities/traveler/travel_preferences.js';
import { AIRPORT_CITIES } from './airport_cities.js';
import { lookupByAirportCode } from './airport_code_aliases.js';
import {
  AIRPORT_CITIES_EN,
  AIRPORT_CITIES_ES,
  METRO_CITIES_EN,
  METRO_CITIES_ES,
} from './airport_cities_i18n.js';
import { AIRPORT_CITIES_WORLD } from './airport_cities_world.js';
import { isBrazilianAirport } from './airport_timezone.js';
import { foldAlias } from './fold_alias.js';
import { isMetroCode, metroCityOf } from './metro_city.js';

/**
 * Geo reference: resolve an airport reference to its city name / IATA code.
 *
 * The voucher/trip source is inconsistent — a destination or segment airport arrives as a bare
 * IATA (`"SDU"`), an IATA-prefixed display label (`"SDU - RIO DE JANEIRO"`), or a plain city name.
 * These helpers normalize all three shapes so display consumers (wallet pass hero, trip
 * destination-image label) share one authority instead of each re-deriving IATA→city.
 *
 * Two-tier lookup: the hand-curated pt-BR map (`AIRPORT_CITIES`, accented display names) wins;
 * the generated world map (`AIRPORT_CITIES_WORLD`, ~7k airports, dataset spelling — unaccented,
 * raw casing) covers the rest, returned as-is: no casing rule runs on it. The Live Activity card
 * and the landed greeting show the name unprocessed, so an airport travelers fly gets its polish
 * as a curated entry instead.
 *
 * Pure (no IO): compile-time table lookups.
 */

const BARE_IATA = /^[A-Za-z]{3}$/;
// IATA code followed by a dash and the source's city text: "SDU - RIO DE JANEIRO".
const IATA_PREFIX = /^([A-Za-z]{3})\s*[-–—]\s*(.+)$/;
// A leading 3-letter IATA code (bare or prefixed), the rest of the string ignored.
const LEADING_IATA = /^\s*([A-Za-z]{3})\b/;

/**
 * The leading IATA code of an airport reference (`"GRU - SAO PAULO"` → `"GRU"`, `"GRU"` → `"GRU"`),
 * or `null` when the string does not start with a 3-letter code.
 */
export function iataOf(airport: string | null | undefined): string | null {
  if (!airport) return null;
  return LEADING_IATA.exec(airport)?.[1]?.toUpperCase() ?? null;
}

/**
 * City display name for an airport reference. A recognized airport code (bare or prefixed) resolves
 * to the curated accented pt-BR name (`"SDU"` / `"SDU - RIO DE JANEIRO"` → `"Rio de Janeiro"`); an
 * unrecognized prefixed code falls back to the source's city part (`"XYZ - Foo"` → `"Foo"`); a plain
 * city name passes through unchanged; an unrecognized bare code yields `null`.
 */
export function cityByAirport(airport: string | null | undefined): string | null {
  if (!airport) return null;
  const s = airport.trim();
  if (!s) return null;

  const prefixed = IATA_PREFIX.exec(s);
  if (prefixed) {
    const code = prefixed[1]?.toUpperCase() ?? '';
    return cityByIata(code) ?? prefixed[2]?.trim() ?? null;
  }

  const code = s.toUpperCase();
  if (BARE_IATA.test(code)) return cityByIata(code);

  return s;
}

/** Curated pt-BR name first, metro ("all airports") codes second, generated world map
 *  last, `null` for an unknown code. The metro tier sits ABOVE the world map because
 *  the generated dataset holds at least one metro code as a stray airport entry
 *  (`SAO: "Sao Paulo"`, unaccented) — the curated `METRO_AREAS` spelling must win.
 *  Callers that need to DISTINGUISH a metro from a real airport use `isMetroCode`;
 *  here a metro simply resolves to the city it stands for. */
function cityByIata(code: string): string | null {
  return lookupByAirportCode(
    code,
    (iata) => AIRPORT_CITIES[iata] ?? metroCityOf(iata) ?? AIRPORT_CITIES_WORLD[iata],
  );
}

/**
 * City name for an airport reference in the MESSAGE locale (#3763): a notification rendered
 * in `en` must not carry the legacy producer's pt-BR free text (`"GRU - SAO PAULO"`). Only the
 * IATA code is read — bare or prefixed — and the source's city text is always discarded:
 *
 *  - known code → the city in `locale` (`"JFK"` → `Nova York` / `New York` / `Nueva York`);
 *  - unknown code → the code itself (`"XYZ - Foo"` → `"XYZ"`), neutral in every language;
 *  - not an airport reference (`"Rio de Janeiro"`, a street address) → `null`, so the caller
 *    decides whether the free text may be shown in that locale.
 */
export function localizedCityByAirport(
  airport: string | null | undefined,
  locale: Locale,
): string | null {
  const code = airportCodeOf(airport);
  if (code === null) return null;
  const city = locale === 'pt-BR' ? cityByIata(code) : foreignCityByIata(code, locale);
  return city ?? code;
}

/**
 * The city of an airport reference in `locale`, or `null` when the catalog does not know it:
 * `localizedCityByAirport` answers an unknown code with the code itself, which is not a city
 * (the Live Activity card would repeat "ZZZ" under the "ZZZ" code). A city whose name differs
 * from its code only in casing ("Ufa" × UFA) is a real city.
 */
export function knownCityByAirport(
  airport: string | null | undefined,
  locale: Locale,
): string | null {
  const city = localizedCityByAirport(airport, locale);
  return city === null || city === iataOf(airport) ? null : city;
}

/** The IATA of a bare (`"GRU"`) or prefixed (`"GRU - SAO PAULO"`) reference; free text → `null`. */
function airportCodeOf(airport: string | null | undefined): string | null {
  const s = airport?.trim() ?? '';
  const code = IATA_PREFIX.exec(s)?.[1] ?? (BARE_IATA.test(s) ? s : null);
  return code?.toUpperCase() ?? null;
}

/** `es` curated → `en` curated → native curated name → generated world map. */
function foreignCityByIata(code: string, locale: 'en' | 'es'): string | null {
  return lookupByAirportCode(code, (iata) => {
    const es = locale === 'es' ? (AIRPORT_CITIES_ES[iata] ?? METRO_CITIES_ES[iata]) : undefined;
    return (
      es ??
      AIRPORT_CITIES_EN[iata] ??
      METRO_CITIES_EN[iata] ??
      nativeCuratedCity(iata) ??
      AIRPORT_CITIES_WORLD[iata]
    );
  });
}

/** The curated pt-BR name when it is the city's OWN name, valid in any locale: every Brazilian
 *  airport (`Brasília`, `Natal` — the dataset names NAT's municipality), and any airport whose
 *  curated entry only fixes the dataset's accents/casing (`Bogota` → `Bogotá`, `Foz Do Iguacu`
 *  → `Foz do Iguaçu`; IGU's generated time zone is Argentine, so the Brazil test misses it). */
function nativeCuratedCity(code: string): string | undefined {
  const curated = AIRPORT_CITIES[code];
  if (curated === undefined) return undefined;
  if (isBrazilianAirport(code)) return curated;
  const world = AIRPORT_CITIES_WORLD[code];
  return world !== undefined && foldAlias(world) === foldAlias(curated) ? curated : undefined;
}

/**
 * Every name the maps give the city an airport code serves, for MATCHING what a person typed —
 * `cityByAirport` shows one name, and a curated exonym must not hide the one people type
 * (`"LHR"` → `Londres` and the dataset's `London`). A real airport yields its curated pt-BR,
 * generated world, `en` and `es` names; a metro code its city in each message locale (`"LON"` →
 * `Londres`, `London`). Each name once, accents and case aside; `[]` for an unknown code.
 */
export function cityNamesByAirport(iata: string | null | undefined): string[] {
  const code = iata?.trim().toUpperCase() ?? '';
  const names = isMetroCode(code)
    ? [metroCityOf(code), METRO_CITIES_EN[code], METRO_CITIES_ES[code]]
    : [
        AIRPORT_CITIES[code],
        AIRPORT_CITIES_WORLD[code],
        AIRPORT_CITIES_EN[code],
        AIRPORT_CITIES_ES[code],
      ];
  const byFolded = new Map<string, string>();
  for (const name of names) {
    if (name && !byFolded.has(foldAlias(name))) byFolded.set(foldAlias(name), name);
  }
  return [...byFolded.values()];
}

/**
 * O aeroporto REAL de um segmento voado (`"GRU"`) satisfaz o código que a pessoa
 * PEDIU (`"GRU"` ou o metro `"SAO"`)? Igualdade de código sempre vale; um metro
 * pedido aceita qualquer aeroporto que sirva a sua cidade (`GRU`/`CGH`/`VCP` ⊂
 * `SAO`). A recíproca NÃO vale: aeroporto real pedido não aceita o vizinho
 * (`CGH` ≠ `GRU`) — quem pediu Guarulhos pediu Guarulhos. Um metro do lado do
 * aeroporto (`airport` = `"SAO"`) só bate por igualdade, porque segmento voado
 * nunca é metro. Entrada ausente/vazia nunca bate.
 */
export function airportMatchesCode(
  airport: string | null | undefined,
  code: string | null | undefined,
): boolean {
  const a = iataOf(airport);
  const c = iataOf(code);
  if (!a || !c) return false;
  if (a === c) return true;
  if (!isMetroCode(c)) return false;
  const city = cityByAirport(a);
  return city !== null && city === metroCityOf(c);
}
